// Shared CRM session state (workspace, permissions, lookups) + helpers.
import Vue from 'vue'
import moment from 'jalali-moment'
import i18n from '@/i18n'
import api, { setWorkspaceId, storedWorkspaceId } from './crmApi'

export const crm = Vue.observable({
  ready: false,
  loading: false,
  error: null,
  noWorkspace: false,
  workspaces: [],
  invites: [],
  me: null,
  pipelines: [],
  members: [],
  sources: [],
  lossReasons: [],
  statuses: { lead: [], person: [], company: [] },
  users: {} // users.id -> display name (members, picker picks, API rows)
})

export function can (perm) {
  const me = crm.me
  if (!me) return false
  if (me.is_owner) return true
  return (me.permissions || []).includes(perm)
}

export function pick (list) {
  return Array.isArray(list) ? list : (list && Array.isArray(list.data) ? list.data : [])
}

async function loadLookups () {
  const [pipelines, members, sources, reasons, statuses] = await Promise.all([
    api.pipelines().catch(() => []),
    api.members({ status: 'active' }).catch(() => []),
    api.leadSources().catch(() => []),
    api.lossReasons().catch(() => []),
    api.statuses().catch(() => ({}))
  ])
  crm.pipelines = pick(pipelines)
  crm.members = pick(members)
  crm.sources = pick(sources)
  crm.lossReasons = pick(reasons)
  setStatuses(statuses)
  crm.members.forEach(m => {
    const id = m.user_id || m.id
    const u = m.user || m
    rememberUser(id, u.name || personName(u) || u.mobile)
  })
}

export function setStatuses (res) {
  const r = res || {}
  const sort = l => pick(l).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  crm.statuses = { lead: sort(r.lead), person: sort(r.person), company: sort(r.company) }
}

export async function reloadPipelines () {
  crm.pipelines = pick(await api.pipelines())
  try { crm.me = await api.me() } catch (e) { /* usage counters only */ }
}

export async function reloadStatuses () {
  setStatuses(await api.statuses())
}

/** Status meta from the workspace list (doc §6.5.1); falls back to the
 *  fixed palette for deal statuses and unknown keys. */
export function statusMeta (key, entity = 'lead') {
  const list = crm.statuses[entity] || []
  const s = list.find(x => x.key === key)
  if (s) return { key, title: s.title, color: s.color || STATUS_COLORS[key] || '#8a94a6', category: s.category }
  return { key, title: null, color: STATUS_COLORS[key] || '#8a94a6', category: LEGACY_CATEGORY[key] || 'open' }
}

export function leadStatuses () {
  if (crm.statuses.lead.length) return crm.statuses.lead
  return LEAD_STATUSES.map((key, i) => ({ key, title: null, color: STATUS_COLORS[key], category: LEGACY_CATEGORY[key] || 'open', sort_order: i, is_system: true }))
}

const LEGACY_CATEGORY = { converted: 'won', unqualified: 'lost', rejected: 'lost', archived: 'archived' }

export function rememberUser (id, name) {
  if (!id || !name) return
  if (crm.users[id] !== name) Vue.set(crm.users, id, name)
}

/** Learns owner names from API rows (owner_name / owner object). */
export function learnOwners (rows) {
  (rows || []).forEach(r => {
    if (!r) return
    if (r.owner_user_id && r.owner_name) rememberUser(r.owner_user_id, r.owner_name)
    if (r.owner && r.owner.id) rememberUser(r.owner.id, r.owner.name || personName(r.owner))
  })
}

export function personName (u) {
  if (!u) return ''
  return [u.first_name || u.firstname, u.last_name || u.lastname].filter(Boolean).join(' ')
}

export async function bootstrap (force = false) {
  if (crm.ready && !force) return
  crm.loading = true
  crm.error = null
  try {
    const list = pick(await api.workspaces())
    crm.workspaces = list
    if (!list.length) {
      crm.noWorkspace = true
      crm.invites = pick(await api.myInvites().catch(() => []))
      return
    }
    crm.noWorkspace = false
    const saved = storedWorkspaceId()
    const ws = list.find(w => w.id === saved) || list.find(w => w.is_my_default) || list[0]
    setWorkspaceId(ws.id)
    crm.me = await api.me()
    await loadLookups()
    crm.ready = true
  } catch (e) {
    const res = e && e.response
    if (res && res.status === 404 && res.data && res.data.errors && res.data.errors.code === 'no_workspace') {
      crm.noWorkspace = true
    } else {
      crm.error = (res && res.data && res.data.message) || e.message
    }
  } finally {
    crm.loading = false
  }
}

export async function switchWorkspace (id) {
  crm.loading = true
  try {
    setWorkspaceId(id)
    crm.me = await api.switchWorkspace(id)
    crm.pipelines = []
    crm.members = []
    await loadLookups()
    crm.ready = true
  } finally {
    crm.loading = false
  }
}

// ---- lookups -------------------------------------------------------------

export function memberName (userId) {
  if (!userId) return ''
  return crm.users[userId] || ('#' + userId)
}

export function memberOptions () {
  return crm.members.map(m => ({ value: m.user_id || m.id, text: memberName(m.user_id || m.id) }))
}

export function initials (name) {
  const s = String(name || '').trim()
  if (!s) return '?'
  const parts = s.split(/\s+/)
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase()
}

const AVATAR_COLORS = ['#2166d1', '#0f9d76', '#d9822b', '#8e44ad', '#c0392b', '#16a085', '#5c6bc0', '#00838f']
export function avatarColor (seed) {
  const s = String(seed || '')
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

export function sourceTitle (id) {
  const s = crm.sources.find(x => x.id === id)
  return s ? s.title : ''
}

// ---- enums & palette (fixed front palette, doc §7 "رنگ‌بندی کنترل‌شده") ----

export const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'unqualified', 'converted', 'rejected', 'archived']
export const DEAL_STATUSES = ['open', 'paused', 'won', 'lost']
export const PRIORITIES = ['low', 'normal', 'high', 'urgent']
export const ACTIVITY_TYPES = ['call', 'meeting', 'task', 'sms', 'email', 'message', 'visit', 'note']

export const STATUS_COLORS = {
  new: '#2166d1',
  contacted: '#00838f',
  qualified: '#0f9d76',
  unqualified: '#8a94a6',
  converted: '#6a4fd1',
  rejected: '#c0392b',
  archived: '#9aa4b2',
  open: '#2166d1',
  paused: '#d9822b',
  won: '#0f9d76',
  lost: '#c0392b'
}

export const PRIORITY_COLORS = {
  low: '#8a94a6',
  normal: '#2166d1',
  high: '#d9822b',
  urgent: '#c0392b'
}

export const ACTIVITY_ICONS = {
  call: 'mdi-phone-outline',
  meeting: 'mdi-account-group-outline',
  task: 'mdi-checkbox-marked-circle-outline',
  sms: 'mdi-message-text-outline',
  email: 'mdi-email-outline',
  message: 'mdi-chat-outline',
  visit: 'mdi-map-marker-outline',
  note: 'mdi-note-text-outline',
  feedback: 'mdi-star-outline',
  custom: 'mdi-shape-outline'
}

// ---- formatting ----------------------------------------------------------

export function isFa () {
  return i18n.locale !== 'en'
}

export function money (v, short = false) {
  if (v === null || v === undefined || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  if (short) {
    const abs = Math.abs(n)
    if (abs >= 1e9) return trim(n / 1e9) + (isFa() ? ' میلیارد' : 'B')
    if (abs >= 1e6) return trim(n / 1e6) + (isFa() ? ' میلیون' : 'M')
    if (abs >= 1e3) return trim(n / 1e3) + (isFa() ? ' هزار' : 'K')
  }
  return n.toLocaleString(isFa() ? 'fa-IR' : 'en-US')
}

function trim (n) {
  return Number(n.toFixed(1)).toLocaleString(isFa() ? 'fa-IR' : 'en-US')
}

export function fmtDate (v, withTime = false) {
  if (!v) return ''
  const m = moment(v, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601])
  if (!m.isValid()) return v
  if (isFa()) return m.locale('fa').format(withTime ? 'jD jMMMM، HH:mm' : 'jD jMMMM jYYYY')
  return m.locale('en').format(withTime ? 'D MMM, HH:mm' : 'D MMM YYYY')
}

export function relDays (v) {
  if (!v) return null
  const m = moment(v, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD', moment.ISO_8601])
  if (!m.isValid()) return null
  return m.startOf('day').diff(moment().startOf('day'), 'days')
}

/** follow-up state: 'none' | 'overdue' | 'today' | 'future' */
export function followState (v) {
  const d = relDays(v)
  if (d === null) return 'none'
  if (d < 0) return 'overdue'
  if (d === 0) return 'today'
  return 'future'
}

/** Name of a lead/person. Leads keep identity in `users` (doc §6.5), so
 *  read the row first and fall back to the joined `user`. */
export function fullName (r) {
  if (!r) return ''
  return personName(r) || personName(r.user) || (r.user && r.user.name) || ''
}

export function leadCompany (r) {
  if (!r) return ''
  return r.company_name || (r.user && (r.user.business_name || r.user.company_name)) || ''
}

export function leadPhone (r) {
  if (!r) return ''
  return r.mobile || r.phone || (r.user && (r.user.mobile || r.user.phone)) || ''
}

export function leadEmail (r) {
  if (!r) return ''
  return r.email || (r.user && r.user.email) || ''
}

export function dealCustomer (d) {
  if (!d) return ''
  const p = d.person ? fullName(d.person) : ''
  const c = d.company ? d.company.name : ''
  return [p, c].filter(Boolean).join(' · ')
}

export function nowSql (addDays = 0, hour = null) {
  const m = moment().add(addDays, 'days')
  if (hour !== null) m.hour(hour).minute(0).second(0)
  return m.locale('en').format('YYYY-MM-DD HH:mm:ss')
}

/** Toolbar filter state → list query params (same params for every view). */
export function toParams (f, entity) {
  const p = {}
  if (f.search) p.search = f.search
  ;['status', 'priority'].forEach(k => { if (f[k] && f[k].length) p[k] = f[k] })
  if (entity === 'lead' && f.source_id && f.source_id.length) p.source_id = f.source_id
  if (entity === 'lead' && f.status_category && f.status_category.length) p.status_category = f.status_category
  const owners = (f.owner_user_id || []).slice()
  if (f.mine && crm.me && !owners.includes(crm.me.user_id)) owners.push(crm.me.user_id)
  // the picker stores {id, name} objects; the API wants ids
  for (let i = 0; i < owners.length; i++) if (owners[i] && typeof owners[i] === 'object') owners[i] = owners[i].id
  if (owners.length) p.owner_user_id = owners
  if (f.unassigned) p.unassigned = 1
  if (f.without_follow_up) p.without_follow_up = 1
  if (entity === 'lead' && f.not_contacted) p.not_contacted = 1
  if (entity === 'deal' && f.stale_days) p.stale_days = f.stale_days
  if (entity === 'deal' && f.min_amount) p.min_amount = f.min_amount
  if (entity === 'deal' && f.max_amount) p.max_amount = f.max_amount
  if (f.fromDate) p.fromDate = f.fromDate
  if (f.toDate) p.toDate = f.toDate
  if (f.include_archived) p.include_archived = 1
  return p
}

export function download (blob, name) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
